// Lightweight client-side geo detection with localStorage caching.

export type GeoInfo = {
  countryCode: string; // ISO-2 e.g. "MA", "FR"
  currency: string; // ISO-4217 e.g. "EUR", "MAD"
  language: "en" | "fr" | "es" | "nl" | "ar";
};

const CACHE_KEY = "bwt:geo";
const CACHE_TTL = 24 * 60 * 60 * 1000;

// Country -> default currency
const CURRENCY_BY_COUNTRY: Record<string, string> = {
  MA: "MAD",
  US: "USD", CA: "CAD", GB: "GBP", AU: "AUD", NZ: "NZD",
  CH: "CHF", SE: "SEK", NO: "NOK", DK: "DKK", PL: "PLN",
  CZ: "CZK", HU: "HUF", JP: "JPY", CN: "CNY", HK: "HKD",
  SG: "SGD", IN: "INR", BR: "BRL", MX: "MXN", AR: "ARS",
  AE: "AED", SA: "SAR", QA: "QAR", KW: "KWD", BH: "BHD",
  OM: "OMR", JO: "JOD", LB: "LBP", EG: "EGP", DZ: "DZD",
  TN: "TND", IL: "ILS", TR: "TRY", RU: "RUB", ZA: "ZAR",
  NG: "NGN", KE: "KES", UA: "UAH", RO: "RON",
};
// EU / default fallback -> EUR
const EUR_COUNTRIES = new Set([
  "FR","ES","DE","IT","NL","BE","PT","IE","FI","AT","GR","LU","SK","SI","EE","LV","LT","CY","MT",
]);

// Country -> preferred app language
const LANG_BY_COUNTRY: Record<string, GeoInfo["language"]> = {
  FR: "fr", BE: "fr", LU: "fr", CH: "fr", MC: "fr",
  ES: "es", MX: "es", AR: "es",
  NL: "nl",
  MA: "fr", DZ: "fr", TN: "fr",
  // Arabic-speaking markets (Gulf + Levant + Egypt) default to Arabic
  SA: "ar", AE: "ar", QA: "ar", KW: "ar", BH: "ar", OM: "ar",
  JO: "ar", LB: "ar", EG: "ar", IQ: "ar", YE: "ar", SY: "ar", PS: "ar", SD: "ar", LY: "ar",
};

const SUPPORTED: GeoInfo["language"][] = ["en", "fr", "es", "nl", "ar"];

export function detectBrowserLanguage(): GeoInfo["language"] {
  if (typeof navigator === "undefined") return "en";
  const langs = (navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language || "en"]
  ).map((l) => l.toLowerCase().slice(0, 2));
  for (const l of langs) {
    if ((SUPPORTED as string[]).includes(l)) return l as GeoInfo["language"];
  }
  return "en";
}

function currencyFor(country: string): string {
  if (CURRENCY_BY_COUNTRY[country]) return CURRENCY_BY_COUNTRY[country];
  if (EUR_COUNTRIES.has(country)) return "EUR";
  return "EUR";
}

function readCache(): GeoInfo | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at: number; data: GeoInfo };
    if (Date.now() - parsed.at > CACHE_TTL) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(data: GeoInfo) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data }));
  } catch {
    /* ignore */
  }
}

export async function detectGeo(): Promise<GeoInfo> {
  if (typeof window === "undefined") {
    return { countryCode: "MA", currency: "MAD", language: "en" };
  }
  const cached = readCache();
  if (cached) return cached;

  const browserLang = detectBrowserLanguage();
  let countryCode = "MA";

  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 2500);
    const res = await fetch("https://ipapi.co/json/", { signal: ctrl.signal });
    clearTimeout(t);
    if (res.ok) {
      const j = (await res.json()) as { country_code?: string };
      if (j.country_code && /^[A-Z]{2}$/.test(j.country_code)) {
        countryCode = j.country_code;
      }
    }
  } catch {
    /* network failure — use defaults */
  }

  const info: GeoInfo = {
    countryCode,
    currency: currencyFor(countryCode),
    language: LANG_BY_COUNTRY[countryCode] || browserLang,
  };
  writeCache(info);
  return info;
}
